import {
  XYCHART_DEFAULT_CANVAS_HEIGHT,
  XYCHART_MAX_HEIGHT,
  XYCHART_MIN_HEIGHT,
  XYCHART_MIN_WIDTH,
  XYCHART_POPOVER_OFFSET,
  XYCHART_POPOVER_WIDTH,
  XYCHART_SERIES_COLORS,
} from './xychart-constants';
import {
  XYCHART_SERIES_TYPE,
  type XYChartLayout,
  type XYChartParsed,
} from './xychart-types';
import type { XYChartHoverEntry } from './XYChartHoverPopover';
import { CmarkdownvisualType } from '../Cmarkdownvisual/Cmarkdownvisual.types';

const CHART_VALUE = "chart";
const XYCHART_VALUE = "xychart";

const SAFE_SVG_ID_REGEX = /[^a-zA-Z0-9_-]/g;
const SVG_ID_EDGE_DASHES_REGEX = /^-+|-+$/g;
const SVG_ID_HASH_OFFSET_BASIS = 2166136261;
const SVG_ID_HASH_PRIME = 16777619;
const NUMBER_LOCALE = 'en-US';
const XYCHART_METRIC_UNIT = 'SERIES';
// The mixed-series label is the form's own visual type; the single-series
// variants below narrow it for the reader.
const CHART_TYPE_LABEL_MIXED = CmarkdownvisualType.XyChart;
const CHART_TYPE_LABEL_LINE = 'Line Chart';
const CHART_TYPE_LABEL_BAR = 'Bar Chart';
const HORIZONTAL_POPOVER_Y_OFFSET = 22;
const VERTICAL_POPOVER_TOP_OFFSET = 8;
const POPOVER_CANVAS_EDGE_GAP = 8;
const POPOVER_MIN_TOP = 4;

export interface XYChartViewportSize {
  width: number;
  height: number;
}

export interface XYChartLayoutDimensions {
  containerWidth: number;
  containerHeight: number;
}

export interface XYChartMetricModel {
  value: string;
  unit: string;
}

export interface XYChartChromeModel {
  chartKindLabel: string;
  metric: XYChartMetricModel;
}

export interface XYChartPopoverPosition {
  left: number;
  top: number;
}

export interface XYChartPopoverModel {
  category: string;
  position: XYChartPopoverPosition;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function sanitizeSvgIdSegment(value: string): string {
  const sanitized = value
    .replace(SAFE_SVG_ID_REGEX, '-')
    .replace(SVG_ID_EDGE_DASHES_REGEX, '');

  return sanitized || CHART_VALUE;
}

function hashSvgIdSource(value: string): string {
  let hash = SVG_ID_HASH_OFFSET_BASIS;
  for (let characterIndex = 0; characterIndex < value.length; characterIndex += 1) {
    hash ^= value.charCodeAt(characterIndex);
    hash = Math.imul(hash, SVG_ID_HASH_PRIME);
  }

  return (hash >>> 0).toString(36);
}

export function createXYChartSvgId(input: {
  content: string;
  parsed: XYChartParsed | null;
}): string {
  const semanticSource = input.parsed
    ? [
        input.parsed.title ?? XYCHART_VALUE,
        ...input.parsed.xAxis.categories,
        ...input.parsed.series.map((series) => series.label),
      ].join('-')
    : input.content;
  const semanticSegment = sanitizeSvgIdSegment(semanticSource).slice(0, 72);

  return `xychart-${semanticSegment}-${hashSvgIdSource(input.content)}`;
}

export function resolveXYChartLayoutDimensions(input: {
  viewportSize: XYChartViewportSize;
}): XYChartLayoutDimensions {
  return {
    containerWidth: Math.max(input.viewportSize.width, XYCHART_MIN_WIDTH),
    containerHeight: clamp(
      input.viewportSize.height || XYCHART_DEFAULT_CANVAS_HEIGHT,
      XYCHART_MIN_HEIGHT,
      XYCHART_MAX_HEIGHT
    ),
  };
}

export function selectXYChartChrome(input: {
  parsed: XYChartParsed;
}): XYChartChromeModel {
  const lineSeriesCount = input.parsed.series.filter(
    (series) => series.type === XYCHART_SERIES_TYPE.Line
  ).length;
  const barSeriesCount = input.parsed.series.filter(
    (series) => series.type === XYCHART_SERIES_TYPE.Bar
  ).length;

  return {
    chartKindLabel: selectChartKindLabel({ lineSeriesCount, barSeriesCount }),
    metric: {
      value: input.parsed.series.length.toString(),
      unit: XYCHART_METRIC_UNIT,
    },
  };
}

function selectChartKindLabel(input: {
  lineSeriesCount: number;
  barSeriesCount: number;
}): string {
  if (input.lineSeriesCount > 0 && input.barSeriesCount > 0) {
    return CHART_TYPE_LABEL_MIXED;
  }

  if (input.lineSeriesCount > 0) {
    return CHART_TYPE_LABEL_LINE;
  }

  if (input.barSeriesCount > 0) {
    return CHART_TYPE_LABEL_BAR;
  }

  return CHART_TYPE_LABEL_MIXED;
}

export function selectXYChartHoverEntries(input: {
  parsed: XYChartParsed;
  hoveredCategoryIndex: number | null;
}): XYChartHoverEntry[] {
  const { hoveredCategoryIndex } = input;
  if (hoveredCategoryIndex == null) return [];

  return input.parsed.series.map((series) => ({
    seriesIndex: series.colorIndex,
    label: series.label,
    value: series.data[hoveredCategoryIndex] ?? 0,
    color: XYCHART_SERIES_COLORS[series.colorIndex % XYCHART_SERIES_COLORS.length],
  }));
}

export function selectXYChartPopoverModel(input: {
  parsed: XYChartParsed;
  layout: XYChartLayout | null;
  hoveredCategoryIndex: number | null;
}): XYChartPopoverModel | null {
  if (input.layout == null || input.hoveredCategoryIndex == null) return null;

  const center = input.layout.categoryCenters[input.hoveredCategoryIndex];
  if (center == null) return null;

  return {
    category:
      input.parsed.xAxis.categories[input.hoveredCategoryIndex] ??
      String(input.hoveredCategoryIndex + 1),
    position: selectXYChartPopoverPosition({ layout: input.layout, center }),
  };
}

function selectXYChartPopoverPosition(input: {
  layout: XYChartLayout;
  center: number;
}): XYChartPopoverPosition {
  if (input.layout.horizontal) {
    return {
      left:
        input.layout.margins.left +
        input.layout.plotWidth +
        XYCHART_POPOVER_OFFSET,
      top: input.layout.margins.top + input.center - HORIZONTAL_POPOVER_Y_OFFSET,
    };
  }

  const left =
    input.layout.margins.left + input.center - XYCHART_POPOVER_WIDTH / 2;
  const top = input.layout.margins.top - VERTICAL_POPOVER_TOP_OFFSET;
  const maxLeft = Math.max(
    0,
    input.layout.width - XYCHART_POPOVER_WIDTH - POPOVER_CANVAS_EDGE_GAP
  );

  return {
    left: clamp(left, POPOVER_CANVAS_EDGE_GAP, maxLeft),
    top: Math.max(POPOVER_MIN_TOP, top),
  };
}

export function formatXYChartHoverValue(value: number): string {
  return new Intl.NumberFormat(NUMBER_LOCALE, {
    maximumFractionDigits: 2,
  }).format(value);
}
